import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import "./App.css";
import config from "./config";
import draftStateApi from "./services/draftStateApi";
import { devLog } from "./utils/devLog";
import LoadingSpinner from "./components/LoadingSpinner";
import SeasonCalendarBanner from "./components/SeasonCalendarBanner";
import { WEEK_1_SCORING_START_LABEL } from "./utils/seasonCalendar";
import { getTransactionWindowCopy, getPreseasonWaiverCopy, getWaiverCustomizationNote, resolveUnlockSettings } from "./utils/transactionWindowCopy";

export default function Home({ leagueId, navigate, currentUser, showToast, leagueSettings: leagueSettingsFromRoute, onLogout }) {
  const location = useLocation();
  // League settings state
  const [leagueSettings, setLeagueSettings] = useState(null);
  const [draftComplete, setDraftComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastLocationRef = useRef(location.pathname);
  const draftStorageMigratedRef = useRef(false);
  
  // Fetch league settings
  const fetchLeagueSettings = useCallback(async () => {
    if (!leagueId || !currentUser) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${config.API_URL}/leagues/${leagueId}/draft-info`, {
        credentials: 'include'
      });
      
        if (response.ok) {
          const league = await response.json();
          setLeagueSettings({
            id: leagueId,
            name: league.name,
            scoringType: league.scoring_type,
            draftType: league.draft_type,
            salaryCap: league.salary_cap,
            faabBudget: league.faab_budget,
            useFAAB: Boolean(league.use_faab), // Convert integer (0/1) to boolean
            rosterUnlockDay: league.roster_unlock_day,
            teamCount: league.team_count,
            qbCount: league.qb_count || 1,
            wrCount: league.wr_count || 2,
            rbCount: league.rb_count || 2,
            teCount: league.te_count || 1,
            flexCount: league.flex_count || 1,
            flexSilverCount: league.flex_silver_count != null ? league.flex_silver_count : 0,
            flexGoldCount: league.flex_gold_count != null ? league.flex_gold_count : 0,
            benchCount: league.bench_count ?? 5,
            kCount: league.k_count ?? 1,
            playoffTeams: league.playoff_teams || 4,
          });
        }
    } catch (error) {
      console.error('Error fetching league settings:', error);
      showToast('Error loading league settings', 'error');
    } finally {
      setLoading(false);
    }
  }, [leagueId, currentUser, showToast]);
  
  useEffect(() => {
    fetchLeagueSettings();
  }, [fetchLeagueSettings]);
  
  // Refetch settings when navigating back to home page (especially from settings)
  useEffect(() => {
    if (!leagueId || !currentUser) return;
    
    const currentPath = location.pathname;
    const isOnHome = currentPath === `/league/${leagueId}` || currentPath === `/league/${leagueId}/`;
    const wasOnSettings = lastLocationRef.current?.includes('/settings');
    
    // Refetch if we're on home page and either:
    // 1. We just navigated from settings, OR
    // 2. This is the first time we're seeing this path (component mount)
    if (isOnHome && (wasOnSettings || lastLocationRef.current !== currentPath)) {
      fetchLeagueSettings();
    }
    
    lastLocationRef.current = currentPath;
  }, [location.pathname, leagueId, currentUser, fetchLeagueSettings]);
  
  // Check draft completion status (API only; migrate legacy localStorage once)
  useEffect(() => {
    const checkDraftStatus = async () => {
      if (!leagueId || !currentUser) return;

      try {
        if (!draftStorageMigratedRef.current) {
          await draftStateApi.migrateDraftLocalStorageOnly(leagueId, currentUser);
          draftStorageMigratedRef.current = true;
        }
        const data = await draftStateApi.getDraftCompletionStatus(leagueId, currentUser);
        devLog(`Draft completion status: ${data.is_complete}`);
        setDraftComplete(!!data.is_complete);
      } catch (error) {
        console.error('Error checking draft status:', error);
        setDraftComplete(false);
      }
    };

    checkDraftStatus();
    const interval = setInterval(checkDraftStatus, 5000);
    return () => clearInterval(interval);
  }, [leagueId, currentUser]);

  const settingsForCopy = leagueSettingsFromRoute || leagueSettings;
  const transactionWindowCopy = getTransactionWindowCopy({
    ...resolveUnlockSettings(settingsForCopy),
    includeFaab: Boolean(settingsForCopy?.useFAAB),
  });
  const preseasonWaiverCopy = getPreseasonWaiverCopy();
  const waiverCustomizationNote = getWaiverCustomizationNote({ leagueSettings: settingsForCopy });

  return (
    <div className="ffb-bg ffb-league-home">
      <header className="ffb-header ffb-header-compact">
        <h1 className="ffb-header-title">🏈 Fantasy Football Classic™ 🏈</h1>
      </header>
      <div className="ffb-main-content ffb-league-home-content">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <LoadingSpinner text="Loading league..." />
          </div>
        ) : leagueSettings ? (
          <>
            <h2 className="ffb-league-home-heading">The Ultimate Historical Fantasy Football League</h2>
            <SeasonCalendarBanner
              compact
              style={{ maxWidth: 900, margin: "0 auto 12px auto" }}
              leagueSettings={leagueSettingsFromRoute || leagueSettings}
            />
            <div className="ffb-league-nav">
              <button className="ffb-btn" onClick={() => navigate("/projections")} aria-label="Historical Player Projections">Historical Player Projections</button>
              <button className="ffb-btn" onClick={() => navigate(`/league/${leagueId}/standings`)} aria-label="League Standings">League Standings</button>
              {draftComplete ? (
                <button className="ffb-btn" onClick={() => navigate(`/league/${leagueId}/draft-results`)} aria-label="Draft Results">Draft Results</button>
              ) : (
                <button className="ffb-btn" onClick={() => navigate(`/league/${leagueId}/draft`)} aria-label="Draft">Draft</button>
              )}
              <button className="ffb-btn" onClick={() => navigate(`/league/${leagueId}/team`)} aria-label="Team Management">Team Management</button>
              {leagueSettings.useFAAB ? (
                <button className="ffb-btn" onClick={() => navigate(`/league/${leagueId}/faab`)} aria-label="Waivers (FAAB)">Waivers (FAAB)</button>
              ) : (
                <button className="ffb-btn" onClick={() => navigate(`/league/${leagueId}/waivers`)} aria-label="Waivers">Waivers</button>
              )}
              <button className="ffb-btn" onClick={() => navigate(`/league/${leagueId}/results`)} aria-label="Scoreboard">Scoreboard</button>
              <button className="ffb-btn" onClick={() => navigate(`/league/${leagueId}/settings`)} aria-label="League Settings">League Settings</button>
              <button className="ffb-btn" onClick={() => navigate("/")} aria-label="League Selection" style={{ background: '#64748b' }}>League Selection</button>
            </div>
            {currentUser && onLogout ? (
              <div className="ffb-league-account">
                <span className="ffb-league-account-welcome">
                  Welcome, <strong>{currentUser.username}</strong>
                </span>
                <button
                  type="button"
                  onClick={onLogout}
                  className="ffb-league-account-logout"
                  aria-label="Logout"
                >
                  Logout
                </button>
              </div>
            ) : null}
            <div className="ffb-league-intro">
              <div className="ffb-body-text">
                A full season-long fantasy football league — draft, weekly lineups, waivers, and playoffs — powered by real historical NFL stats from 1999–2025.
              </div>
              <div className="ffb-body-text" style={{ marginTop: 8, color: "#64748b" }}>
                This is not a one-game simulation or daily contest.
              </div>
            </div>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <p>League not found or you don't have access.</p>
            <button className="ffb-btn" onClick={() => navigate("/")}>Go to League Selection</button>
          </div>
        )}
        <div className="ffb-card">
          <h2 className="ffb-section-heading">FAQ</h2>
          <div className="ffb-faq-heading">When should we draft?</div>
          <div className="ffb-body-text">
            All leagues follow the same NFL calendar regardless of when you create them. For a full 14-week regular season, schedule your draft before Week 1 scoring begins ({WEEK_1_SCORING_START_LABEL}). If you draft later, scoring still begins on the global calendar — missed early weeks will not have lineups or scores, and there is no per-league start-week setting.
          </div>
          <div className="ffb-faq-heading">How does the league work?</div>
          <div className="ffb-body-text">
            Each week, your team faces off against another league member. Scores use real historical NFL stats for that NFL week number (e.g. Week 1 draws a random past season&apos;s Week 1 game), from 1999–2025. All leagues share the same calendar: Week 1 scoring begins {WEEK_1_SCORING_START_LABEL}; regular season weeks 1–14, playoffs weeks 15–17 (champion by Week 17). Each starter&apos;s score reveals at that player&apos;s game kickoff; kickoff day and time stay hidden until then.
          </div>
          <div className="ffb-faq-heading">What is the default scoring system?</div>
          <div className="ffb-body-text">
            Standard fantasy football scoring: 6 points per rushing/receiving TD, 4 points per passing TD, 1 point per 10 rushing/receiving yards, and 1 point per 25 passing yards. Interceptions are -2 points and fumbles lost are -2 points. Private leagues can alter the default scoring and roster settings.
          </div>
          <div className="ffb-faq-heading">Waivers after the draft (preseason)</div>
          <div className="ffb-body-text">
            {preseasonWaiverCopy}
          </div>
          <div className="ffb-faq-heading">Waivers during the season</div>
          <div className="ffb-body-text">
            {transactionWindowCopy} {waiverCustomizationNote}
          </div>
          <div className="ffb-faq-heading">What is the default roster makeup?</div>
          <div className="ffb-body-text">
            By default, each team starts 1 QB, 2 RB, 2 WR, 1 TE, 1 K, and 1 FLEX (RB/WR/TE), plus 5 bench slots. Private league commissioners can adjust roster sizes and positions when setting up the league. Each week you choose starters and save your lineup. When you save a lineup, each starter&apos;s game for that week is rolled once and kept even if you swap them out and back in before kickoff. Bench players do not score. You may edit starters until that slot&apos;s score reveals. You cannot drop starters whose scores have already revealed this week. Players with pending claims are not available for pickup.
          </div>
          <div className="ffb-faq-heading">What player stats are used?</div>
          <div className="ffb-body-text">
            Only real, historical stats for QBs, RBs, WRs, TEs, and Ks from 1999–2025 are used. Only players with at least 10 games across two or more seasons in our historical database are eligible. When you save a lineup, each starter&apos;s game for that week is rolled once and kept even if you swap them out and back in before kickoff.
          </div>
          <div className="ffb-faq-heading">Can I play with friends?</div>
          <div className="ffb-body-text">
            Yes—in private leagues you can invite friends, run your draft together, and compete for the Fantasy Football Classic™ championship. Public leagues are open to anyone (up to 3 active public leagues per account). You can leave a public league before its draft to free a slot; after the draft starts, your team stays for the season.
          </div>
          <div className="ffb-faq-heading">How much does it cost to participate?</div>
          <div className="ffb-body-text">
            Public leagues are free to join. Private (custom) leagues require a platform service fee paid by the commissioner, separate from any league dues a commissioner may collect outside Fantasy Football Classic™.<br /><br />
            <span style={{ fontWeight: 700, color: '#2563eb' }}>Pricing for the 2026 Inaugural Season (50% Off!)</span><br /><br />
            <ul style={{ color: '#2563eb', fontSize: 16, margin: 0, paddingLeft: 24, marginBottom: 10 }}>
              <li><b>Public Leagues:</b> <span style={{ color: '#059669', fontWeight: 700 }}>FREE</span> — join instantly, no payment required!</li>
              <li><b>Private Leagues:</b> <span style={{ color: '#059669', fontWeight: 700 }}>$40</span> per league (normally <span style={{ color: '#b91c1c', textDecoration: 'line-through' }}>$80</span>) — paid by the commissioner.</li>
            </ul>
            <span style={{ color: '#64748b', fontSize: 15 }}><b>Note:</b> For private leagues, commissioners are solely responsible for collecting any league dues from members. fantasyfootballclassic.com does not facilitate or manage financial transactions between users.</span>
            <br />
            <span style={{ color: '#b91c1c', fontSize: 15, display: 'inline-block', marginTop: 8 }}>
              ⚠️ All payments processed through Stripe for access to fantasyfootballclassic.com services only. We do not manage prize money or league dues. All league-related financial agreements are strictly between participants.
            </span>
          </div>
          <div className="ffb-faq-heading">Community standards</div>
          <div className="ffb-body-text">
            League names, team display names, usernames, and draft chat must stay respectful. Slurs, threats, hate speech, and strong profanity are blocked automatically. Competitive trash talk is fine; harassment is not. Commissioners can address issues within their leagues.
          </div>
          <div className="ffb-faq-heading">What are the age requirements to use Fantasy Football Classic™?</div>
          <div className="ffb-body-text">
            You must be at least 13 years old to create an account and use Fantasy Football Classic™. This is to comply with laws protecting children's online privacy, such as COPPA. If you are under 13, you are not allowed to create an account. If a parent or guardian wishes to participate on behalf of a child under 13, they should create and manage the account themselves.
          </div>
          {/* Data attribution section */}
          <div className="ffb-faq-heading">Data attribution</div>
          <div className="ffb-body-text">
            Historical NFL player and game statistics used for scoring are provided by the open-source{' '}
            <a href="https://github.com/nflverse/nflverse" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>nflverse project</a>, developed by Sebastian Carl, Ben Baldwin, Lee Sharpe, Tan Ho, and John Edwards. This data is made available under the{' '}
            <a href="https://opensource.org/licenses/MIT" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb', textDecoration: 'underline' }}>MIT License</a>.
          </div>
        </div>
      </div>
      <footer
        style={{
          textAlign: "center",
          color: "#94a3b8",
          fontSize: "0.98em",
          marginTop: 40,
          padding: 20,
        }}
      >
        
        <div>
          <button
            onClick={() => navigate("/terms")}
            style={{
              color: "#2563eb",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "0.9em",
              marginRight: 16,
              background: "none",
              border: "none"
            }}
          >
            Terms of Service
          </button>
          <button
            onClick={() => navigate("/privacy")}
            style={{
              color: "#2563eb",
              textDecoration: "underline",
              cursor: "pointer",
              fontSize: "0.9em",
              background: "none",
              border: "none"
            }}
          >
            Privacy Policy
          </button>
        </div>
      </footer>
    </div>
  );
} 